import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import {
  useCreateBusinessRuleMutation,
  useUpdateBusinessRuleMutation,
} from 'features/notification-config/services/businessRules.api';
import type { BusinessRuleRow } from 'features/notification-config/types/businessRule';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';

import {
  MORTGAGE_PRE_EVALUATION_CONFIG,
  type MortgagePreEvaluationConfig,
  type MortgageTopology,
} from '../model/mortgageRules';
import MortgagePreEvaluationFields from './MortgagePreEvaluationFields';

interface Props {
  projectId: number;
  countryCode: string | null | undefined;
  config: MortgagePreEvaluationConfig;
  configRow: BusinessRuleRow | null;
  topology: MortgageTopology;
}

const sortedKey = (codes: string[]): string => [...codes].sort().join(',');

/*
 * MORTGAGE_PRE_EVALUATION_CONFIG is the one rule of the section that is not a switch: it holds which
 * financial entities the project pre-evaluates against, plus the split and demo flags. They all live in
 * the same row, so unlike the switches this card keeps a draft and saves once.
 */
const MortgagePreEvaluationCard = ({ projectId, countryCode, config, configRow, topology }: Props): ReactElement => {
  const [createRule, { isLoading: isCreating }] = useCreateBusinessRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateBusinessRuleMutation();

  const [draft, setDraft] = useState<MortgagePreEvaluationConfig>(config);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(config);
  }, [config]);

  const isSaving = isCreating || isUpdating;
  const isDirty =
    sortedKey(draft.entityCodes) !== sortedKey(config.entityCodes) ||
    draft.isMultiAgent !== config.isMultiAgent ||
    draft.isDemo !== config.isDemo ||
    draft.sendsCapacityPdf !== config.sendsCapacityPdf;

  const handleSave = async (): Promise<void> => {
    if (!isDirty) return;
    setError(null);

    try {
      if (configRow) {
        await updateRule({ id: configRow.id, businessRule: { value: draft, enabled: true } }).unwrap();
        return;
      }
      await createRule({
        name: MORTGAGE_PRE_EVALUATION_CONFIG,
        entityType: 'Project',
        entityId: projectId,
        enabled: true,
        value: draft,
      }).unwrap();
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'No se pudo guardar la configuración de pre-evaluación.'));
    }
  };

  return (
    <section className="flex flex-col space-y-4 rounded-md border border-border p-6">
      <div className="space-y-1">
        <h3 className="text-sm font-medium">Pre-evaluación</h3>
        <p className="text-xs text-text-muted">
          Contra qué entidades financieras se evalúa a los clientes de este proyecto.
        </p>
      </div>

      <MortgagePreEvaluationFields
        countryCode={countryCode}
        value={draft}
        disabled={isSaving}
        topology={topology}
        onChange={setDraft}
      />

      <div className="mt-auto flex items-start justify-end gap-4 pt-4">
        <Button
          type="button"
          onClick={() => {
            void handleSave();
          }}
          disabled={!isDirty || isSaving}
        >
          {isSaving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}
    </section>
  );
};

export default MortgagePreEvaluationCard;
